import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { IOSSwitch } from '../common/IosSwitch';
import { useDispatch, useSelector } from 'react-redux';
import { DRIVER_CATEGORY, PLATE_TYPE, TRANSACTION_TYPE_MAP } from '../docs/constants';
import { useEffect, useState } from 'react';
import { selectTransactionInfo } from '../../redux-state/forms/selectors';
import { UPDATE_TRANSACTION_INFO } from '../../redux-state/forms/types';
import CurrencyTextField from '../common/CurrencyField'

function TransactionInfoForm({ errors}) {
  const [isOptionalFieldVisible, setIsOptionalFieldVisible] = useState(false);
  const transactionInfo = useSelector(selectTransactionInfo);
  const dispatch = useDispatch();

  const handleChange = (key, value) => {
    dispatch({
      type: UPDATE_TRANSACTION_INFO,
      payload: { key, value },
    });
  };

  useEffect(() => {
    if (!transactionInfo?.transactionType) {
      const defaultType = Object.keys(TRANSACTION_TYPE_MAP)[0];
      dispatch({
        type: UPDATE_TRANSACTION_INFO,
        payload: {
          key: 'transactionType',
          value: defaultType,
        },
      });
    }
  }, []);

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="p-4 rounded-xl bg-white text-gray-700">
        <div className="text-lg font-bold mb-2">Transaction Info</div>
        <div className="text-gray-600 mb-4">Add correct transaction details</div>

        <div className="mb-4 w-full max-w-[815px]">
          <FormControl fullWidth required error={!!errors?.transactionType}>
            <InputLabel>Transaction Type</InputLabel>
            <Select
              label="Transaction Type"
              value={transactionInfo?.transactionType}
              onChange={(e) => {
                handleChange('transactionType', e.target.value);
                dispatch({
                  type: 'TRANSACTION_TYPE',
                  payload: e.target.value,
                });
              }}
            >
              {Object.keys(TRANSACTION_TYPE_MAP).map((value) => (
                <MenuItem key={value} value={value}>
                  {TRANSACTION_TYPE_MAP[value]}
                </MenuItem>
              ))}
            </Select>
            {errors?.transactionType && <div className="text-red-500 text-xs mt-1">{errors.transactionType}</div>}
          </FormControl>
        </div>

        <div className="flex flex-col md:flex-row mb-4 gap-4 w-full">
          <div className="flex-1">
            <TextField
              required
              label="Transaction Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={transactionInfo?.transactionDate || ''}
              onChange={(e) => handleChange('transactionDate', e.target.value)}
              error={!!errors?.transactionDate}
              helperText={errors?.transactionDate || ''}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
          </div>

          <div className="flex-1">
            <TextField
              required
              label="Uploaded Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={transactionInfo?.uploadDate || ''}
              onChange={(e) => handleChange('uploadDate', e.target.value)}
              error={!!errors?.uploadDate}
              helperText={errors?.uploadDate || ''}
              InputProps={{ style: { borderRadius: '10px' } }}
            />
          </div>
        </div>

        <div className="flex flex-row justify-between w-full">
          <div className="flex items-center">
            Military
            <FormControlLabel
              control={
                <IOSSwitch
                  sx={{ ml: 3 }}
                  checked={transactionInfo?.military}
                  onChange={(e) => handleChange('military', e.target.checked)}
                />
              }
            />
          </div>

          <div className="flex items-center">
            Plate Transfer
            <FormControlLabel
              control={
                <IOSSwitch
                  sx={{ ml: 3 }}
                  checked={transactionInfo?.plateTransfer}
                  onChange={(e) => handleChange('plateTransfer', e.target.checked)}
                />
              }
            />
          </div>

          <div className="flex items-center">
            Lien
            <FormControlLabel
              control={
                <IOSSwitch
                  sx={{ ml: 3 }}
                  checked={transactionInfo?.lien}
                  onChange={(e) => handleChange('lien', e.target.checked)}
                />
              }
            />
          </div>
        </div>

        <div className="flex items-center mt-4">
          Optional Information
          <FormControlLabel
            control={
              <IOSSwitch
                sx={{ ml: 3 }}
                checked={isOptionalFieldVisible}
                onChange={(e) => setIsOptionalFieldVisible(e.target.checked)}
              />
            }
          />
        </div>

        {isOptionalFieldVisible && (
          <div className="space-y-4 mt-4">
            <div className="flex flex-col md:flex-row gap-4 w-full">
              <FormControl fullWidth>
                <InputLabel>Driver Category</InputLabel>
                <Select
                  label="Driver Category"
                  value={transactionInfo?.driverCategory || ''}
                  onChange={(e) => handleChange('driverCategory', e.target.value)}
                >
                  {Object.entries(DRIVER_CATEGORY).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
             <FormControl fullWidth>
              <InputLabel>Plate Type</InputLabel>
              <Select
                label="Plate Type"
                value={transactionInfo?.plateType || ''}
                onChange={(e) => handleChange('plateType', e.target.value)}
              >
                {Object.entries(PLATE_TYPE).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <TextField
                label="Plate Number"
                variant="outlined"
                fullWidth
                value={transactionInfo?.plateNumber || ''}
                onChange={(e) => handleChange('plateNumber', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />

              <CurrencyTextField
                label="Base Price"
                name="basePrice"
                value={transactionInfo?.basePrice} // store number (or '' when empty)
                onChange={(num) => handleChange('basePrice', num)} // dispatch to Redux
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <CurrencyTextField
                label="Doc Fees"
                name="docFees"
                value={transactionInfo?.docsFees ?? ''}   // keep controlled
                onChange={(num) => handleChange('docsFees', num)}  // num is Number | ''
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              <CurrencyTextField
                label="Additional Fees"
                name="additionalfees"
                value={transactionInfo?.additionalFees}  // keep controlled
                onChange={(num) => handleChange('additionalFees', num)}  // num is Number | ''
                InputProps={{ style: { borderRadius: '10px' } }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <CurrencyTextField
                label="Insurance Packages"
                name="insurancepackages"
                value={transactionInfo?.insurancePackages}   // keep controlled
                onChange={(num) => handleChange('insurancePackages', num)}  // num is Number | ''
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              <CurrencyTextField
                label="Protection Packages"
                name="protectionpackagess"
                value={transactionInfo?.protectionPackages}  // keep controlled
                onChange={(num) => handleChange('protectionPackages', num)}  // num is Number | ''
                InputProps={{ style: { borderRadius: '10px' } }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full">
              <CurrencyTextField
                label="Theft Protection"
                name="theftprotection"
                value={transactionInfo?.theftProtection}   // keep controlled
                onChange={(num) => handleChange('theftProtection', num)}  // num is Number | ''
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              
              <CurrencyTextField
                label="Extended Warranties"
                name="extendedwarranties"
                value={transactionInfo?.extendedWarranties}   // keep controlled
                onChange={(num) => handleChange('extendedWarranties', num)}  // num is Number | ''
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionInfoForm;
